import { useMemo } from 'react';

// ---------------------------------------------------------------------------
// The dual-clock stamp -- the design's signature element (visual design spec
// section 7). Storage is UTC, display is the browser's timezone, and no
// timezone is ever persisted: every schedulable instant is shown as one
// object carrying both readings, never one alone. For an exact schedule (the
// only mode Phase 1 has -- no recurring posting slots) the local time leads
// and the UTC row always keeps its trailing `Z`.
// ---------------------------------------------------------------------------

export type StampProps = {
  /** An RFC 3339 / ISO 8601 instant, always carrying an explicit zone (normally `Z`). */
  instant: string;
  /** An IANA timezone name to render the primary row in. Defaults to the browser's own timezone; tests pass this explicitly for a deterministic result. */
  tz?: string;
  /** The 32px-row variant (design doc 7's "compact variant"): both readings still render, but on one line, with the UTC reading available on hover/focus via `title` in addition to the adjacent visually-hidden text -- never dropped. */
  compact?: boolean;
};

type Parts = { time: string; date: string };

const formatParts = (date: Date, timeZone: string | undefined): Parts => {
  const fmt = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    weekday: 'short',
    day: '2-digit',
    month: 'short',
  });
  const parts = fmt.formatToParts(date);
  const get = (type: string): string => parts.find((p) => p.type === type)?.value ?? '';
  // en-US with hour12:false renders midnight as "24" in some engines; a
  // schedule stamp must never show a 24-hour clock reading "24:00".
  const hour = get('hour') === '24' ? '00' : get('hour');
  return {
    time: `${hour}:${get('minute')}`,
    date: `${get('weekday')} ${get('day')} ${get('month')}`,
  };
};

export const Stamp = ({ instant, tz, compact = false }: StampProps): React.JSX.Element => {
  const date = useMemo(() => new Date(instant), [instant]);
  const local = useMemo(() => formatParts(date, tz), [date, tz]);
  const utc = useMemo(() => formatParts(date, 'UTC'), [date]);

  if (Number.isNaN(date.getTime())) {
    return <span className="stamp mono-meta">—</span>;
  }

  if (compact) {
    return (
      <span className="stamp stamp--compact">
        <span className="stamp__primary">{`${local.time} ${local.date}`}</span>
        <span className="stamp__bound" title={`${utc.time}Z ${utc.date}, Coordinated Universal Time`}>
          {`${utc.time}Z`}
          <span className="vh">, Coordinated Universal Time</span>
        </span>
      </span>
    );
  }

  return (
    <span className="stamp">
      <span className="stamp__primary">
        {`${local.time} `}
        <span className="stamp__date">{local.date}</span>
      </span>
      <span className="stamp__bound">
        {`${utc.time}Z `}
        <span className="stamp__date">{utc.date}</span>
      </span>
    </span>
  );
};
